import { AlertCircle } from "lucide-react";
import React from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";

interface QuoteExpiredModalProps {
  isOpen: boolean;
  onRecalculate: () => void;
  onStartOver: () => void;
  isRecalculating?: boolean;
  /** Why the last re-quote attempt failed, if it did. The modal stays open so it can be retried. */
  recalculateError?: string | null;
}

/**
 * Blocks the wizard once the frozen price lapses. There is no dismiss path — re-quoting or starting over are
 * the only two ways forward, so the close button is suppressed rather than wired to a no-op handler.
 */
export const QuoteExpiredModal: React.FC<QuoteExpiredModalProps> = ({
  isOpen,
  onRecalculate,
  onStartOver,
  isRecalculating = false,
  recalculateError = null,
}) => {
  const { t } = useTranslations("dispersions");

  return (
    <ColocaDialog
      open={isOpen}
      onOpenChange={() => {}}
      showCloseButton={false}
      title={t("quote_expired_modal_title")}
      footer={
        <>
          <ColocaButton variant="ghost" onClick={onStartOver} disabled={isRecalculating}>
            {t("quote_expired_start_over")}
          </ColocaButton>
          <ColocaButton variant="primary" onClick={onRecalculate} loading={isRecalculating}>
            {t("quote_expired_recalculate")}
          </ColocaButton>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <ColocaAlert
          variant="warning"
          icon={AlertCircle}
          heading={t("quote_expired_modal_message")}
          text={t("quote_expired_modal_hint")}
        />
        {/*
          Without this the failed retry was silent: the modal stays open (it must — there is no other way
          back to a usable price) and the only feedback was the button no longer spinning.

          Keeps the primitive's `role="alert"`, unlike the standing step-3 banner which downgrades to
          `status`. This one is an EVENT — the operator just pressed Recalculate and it failed, with focus
          already inside the dialog — which is the case assertive exists for. It is safe to interrupt here
          because the page clears `quote.error` when the modal opens and again on each attempt, so this can
          only ever be the failure of the press that just happened.
        */}
        {recalculateError && <ColocaAlert variant="destructive" icon={AlertCircle} text={recalculateError} />}
      </div>
    </ColocaDialog>
  );
};
